import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { ThemeToggleCompact } from '@/components/theme-toggle'
import { Button } from '@/components/ui/button'
import { Logo } from '@/features/shared/components/Logo'
import { NavLink } from '@/features/shared/components/NavLink'
import { useSession } from '@/lib/auth-client'
import { useScrolled } from '@/shared/hooks/useScrolled'
import { cn } from '@/lib/utils'

interface HeaderProps {
  showGetAccess?: boolean
  /** Lets wide pages (e.g. /models) align the bar with their content width. */
  maxWidthClassName?: string
}

export const NAV_ITEMS = [
  { to: '/models', label: 'Models' },
  { to: '/provider-tiers', label: 'Provider Tiers' },
  { to: '/agents', label: 'Agents' },
  { to: '/blogs', label: 'Blog', search: { page: 1 } },
  { to: '/docs/introduction', label: 'Docs' },
] as const

const DESKTOP_LINK = 'px-3.5 hover:bg-[var(--color-control-surface-hover)]'
const MOBILE_LINK =
  'w-full px-3.5 hover:bg-[var(--color-control-surface-hover)]'

export function Header({
  showGetAccess = true,
  maxWidthClassName = 'max-w-7xl',
}: HeaderProps) {
  const navigate = useNavigate()
  const { data: session } = useSession()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const scrolled = useScrolled()
  const solid = scrolled || isMobileMenuOpen
  const accessDestination = session?.user ? '/dashboard' : '/sign-up'
  const accessLabel = session?.user ? 'Dashboard' : 'Get access'

  const goToAccess = () => {
    navigate({ to: accessDestination })
    setIsMobileMenuOpen(false)
  }

  // Transparent over the hero, then a frosted bar with a bottom hairline once
  // the page scrolls. `glass-panel` sets a `border` shorthand, so the top/side
  // edges are zeroed with `!` utilities.
  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,box-shadow,backdrop-filter] duration-500 ease-brisk',
        solid
          ? 'glass-panel border-t-0! border-x-0!'
          : 'border-transparent bg-transparent',
      )}
    >
      <div className={cn('mx-auto px-6', maxWidthClassName)}>
        <div className="flex h-16 items-center justify-between gap-6">
          <Logo size="sm" />

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                search={'search' in item ? item.search : undefined}
                className={DESKTOP_LINK}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Actions */}
          <div className="hidden items-center gap-3 md:flex">
            <ThemeToggleCompact />
            {showGetAccess && (
              <>
                <span
                  aria-hidden
                  className="h-5 w-px bg-[var(--color-border)]"
                />
                <Button
                  onClick={goToAccess}
                  size="sm"
                  className="px-4 font-semibold"
                >
                  {accessLabel}
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="inline-flex size-11 items-center justify-center rounded-full border border-[var(--color-border)] text-[var(--color-text-secondary)] transition-[background-color,border-color,color,transform] duration-300 ease-brisk hover:border-[var(--color-border-strong)] hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary/50 active:scale-[0.97] md:hidden"
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span className="material-symbols-outlined text-xl">
              {isMobileMenuOpen ? 'close' : 'menu'}
            </span>
          </button>
        </div>

        {/* Mobile Menu — solid surface so page glows don't bleed through the
            translucent bar and read as phantom hover states. */}
        {isMobileMenuOpen && (
          <nav className="-mx-6 border-t border-[var(--color-border)] bg-[var(--color-bg-primary)] px-6 py-3 md:hidden">
            <ul className="flex flex-col gap-0.5">
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    search={'search' in item ? item.search : undefined}
                    className={MOBILE_LINK}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex items-center gap-3 border-t border-[var(--color-border)] pt-3">
              <ThemeToggleCompact />
              {showGetAccess && (
                <Button
                  onClick={goToAccess}
                  className="flex-1 font-semibold tracking-tight"
                >
                  {accessLabel}
                </Button>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  )
}
